import { AvField, AvForm } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { Col, Input, Label, Row, Spinner, UncontrolledPopover, PopoverBody } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import { Accordion } from "react-bootstrap";
import { useEffect, useState } from "react";
import AssetDeatilsImage from "src/modules/data-protection/assets/images/basic-info-gdpr.svg";
import { useMutation, useQuery } from "@tanstack/react-query";
import OtherAssetsService from "src/modules/data-protection/apis/OtherAssetsService";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { RiCheckLine, RiCloseCircleLine } from "react-icons/ri";

const OTHER_ASSET_TYPES = [
  "document",
  "whiteboard",
  "binder",
  "printed_email",
  "other",
];

const useAutoResetMutation = (mutationFn, resetTime = 3000) => {
    const mutation = useMutation(mutationFn);

    useEffect(() => {
        let timer;
        if (mutation.isSuccess || mutation.isError) {
            timer = setTimeout(() => {
                mutation.reset();
            }, resetTime);
        }
        return () => clearTimeout(timer);
    }, [mutation.isSuccess, resetTime]);

    return mutation;
};

const SecondRow = ({
    // General
    t,
    assetId,
    handleFetchSoftwareDetailsQuery,
    // Provider
    selectedProvider,
    setSelectedProvider,
    // Asset Type
    selectedAssetType,
    setSelectedAssetType,
    // Version
    version,
    // Service
    service,
    // Modules
    selectedModules,

    notice,
    setNotice,

    countries,
    selectedHostingLocation,
    setSelectedHostingLocation,
    selectedNoneEUHostingLocation,
    setSelectedNoneEUHostingLocation
}) => {
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [providers, setProviders] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const onUploadProgress = (e, file, setUploadedFiles) => {
        const { total, loaded, lengthComputable } = e;

        if (lengthComputable) {
            const uploadedPercent = (loaded / total) * 100;

            setUploadedFiles((uploadedFiles) => {
                const index = uploadedFiles.findIndex(
                    (f) => f.preview === file.preview
                );

                if (index > -1) {
                    uploadedFiles[index].uploadedPercent = uploadedPercent;

                    return [...uploadedFiles];
                }

                return [...uploadedFiles];
            });
        }
    };

    // Modules
    // Fetch
    const handelFetchModulesListQuery = useQuery({
        queryKey: ["data-protection-other-assets-modules-list", moduleOptions],
        queryFn: async () => {
            const service = OtherAssetsService.getInstance();

            const response = await service.modules();
            const data =
                response && response.length
                    ? response.map((r) => ({
                        label: r.name,
                        value: r.id,
                    }))
                    : [];
            setModuleOptions(data);
            return data;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching asset types."), {
                type: "error",
            });
        },
    });

    // Add to mdules
    const handleCreateModuleMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetsService.getInstance();
            return await service.addModule(payload);
        },
        onSuccess: () => {
            handleFetchSoftwareDetailsQuery();
            handelFetchModulesListQuery.refetch();
            toast(t("New module added successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while adding new module."), {
                type: "error",
            });
        },
    });

    const onCreateModuleOption = (label) => {
        handleCreateModuleMutation.mutate({
            otherAsset: Number(assetId),
            module: label,
        });
    };
    // Remove Module
    const handleRemoveModuleMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetsService.getInstance();
            return await service.removeModule(payload);
        },
        onSuccess: () => {
            handleFetchSoftwareDetailsQuery();
            handelFetchModulesListQuery.refetch();
            toast(t("New module removed successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while removing new module."), {
                type: "error",
            });
        },
    });

    const handleModuleChange = (selectedOptions, actionMeta) => {
        if (actionMeta.action === "remove-value") {
            const { removedValue } = actionMeta;
            handleRemoveModuleMutation.mutate({
                otherAsset: Number(assetId),
                module: Number(removedValue?.value),
            });
        } else {
            const { option } = actionMeta;
            handleCreateModuleMutation.mutate({
                otherAsset: Number(assetId),
                module: option?.label,
            });
        }
    };

    const handleChangeNoticeMutation = useAutoResetMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetsService.getInstance();
            return await service.changeNotice(payload);
        }
    });

    const debouncedHandleNoticeChange = debounce((event) => {
        const newData = event.target.value;
        handleChangeNoticeMutation.mutate({
            asset: Number(assetId),
            notice: newData,
        });
    }, 900);

    useEffect(() => {
        if (handelFetchModulesListQuery.data) {
            const gotModules = handelFetchModulesListQuery.data;
            if (gotModules && Array.isArray(gotModules)) {
                setAssetTypes(
                    gotModules.map((gotModule) => {
                        return {
                            label: gotModule,
                            value: gotModule,
                        };
                    })
                );
            }
        }
    }, [handelFetchModulesListQuery.data]);

    return (
        <Accordion className="mt-4" defaultActiveKey={activeAccordion}>
            <Accordion.Item
                onClick={() => setActiveAccordion("asset-details")}
                className={activeAccordion === "asset-details" ? "active" : ""}
                eventKey={"asset-details"}
            >
                <Accordion.Header>
                    <img src={AssetDeatilsImage} alt="" />
                    <span>{t("Asset Details")}</span>
                </Accordion.Header>

                <Accordion.Body>
                    <Row>
                        <Col sm="12" md='4' className='mb-4'>
                            <Label className="form-label">{t("Asset Name")}:</Label>
                            <Input
                                name="softwareName"
                                type="text"
                                className="form-control disabled"
                                value={service}
                                id="softwareName"
                                disabled
                            />
                        </Col>

                        <Col sm="12" md='4' className='mb-4'>
                            <Label className="form-label">{t("Type")}:</Label>
                            <Input
                                name="asset-type"
                                type="select"
                                className="form-control"
                                value={selectedAssetType || ""}
                                id="asset-type"
                                disabled
                            >
                                {OTHER_ASSET_TYPES.map((type) => {
                                    return (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    );
                                })}
                            </Input>
                        </Col>

                        <Col sm="12" md='4' className='mb-4'>
                            <Label className="form-label">
                                {t("Asset Module")}:
                                <>
                                    <span
                                        id="section-info-button-other-asset-module-details"
                                        className="section-info-button"
                                    >
                                        <i className="ri-information-line" />
                                    </span>

                                    <UncontrolledPopover
                                        placement="bottom"
                                        trigger="hover"
                                        target="section-info-button-other-asset-module-details"
                                    >
                                        <PopoverBody>
                                            <span>
                                                {t(
                                                    "An Asset Module is an optional label used to tag a part or area of this other asset. You can select existing modules or create new ones to organize the asset record."
                                                )}
                                            </span>
                                        </PopoverBody>
                                    </UncontrolledPopover>
                                </>
                            </Label>
                            <CreatableSelect
                                className="mt-1 text-capitalize"
                                placeholder={t("Select") + "..."}
                                options={moduleOptions}
                                menuPortalTarget={document.body}
                                isMulti
                                onCreateOption={onCreateModuleOption}
                                onChange={handleModuleChange}
                                value={selectedModules}
                                isClearable={false}
                            />
                        </Col>

                        <Col sm='12'>
                            <Label className="form-label">{t("Description")}:</Label>
                            <AvForm>
                                <AvField
                                    name='notice'
                                    errorMessage={t("This field cannot be blank")}
                                    type="textarea"
                                    value={notice}
                                    // onChange={debouncedHandleNoticeChange}
                                    disabled
                                />

                                {handleChangeNoticeMutation.isLoading && (
                                    <>
                                        <Spinner size='sm' color="info" />
                                        <small className="text-info ms-1">
                                            {t("Saving")}...
                                        </small>
                                    </>
                                )}

                                {handleChangeNoticeMutation.isError && (
                                    <small className="text-danger">
                                        <RiCloseCircleLine size="16" className="me-1" />
                                        {t("Unable to save changes")}
                                    </small>
                                )}

                                {handleChangeNoticeMutation.isSuccess && (
                                    <small className="text-success">
                                        <RiCheckLine size="16" className="me-1" />
                                        {t("Changes saved")}
                                    </small>
                                )}
                            </AvForm>
                        </Col>
                    </Row>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default withNamespaces()(SecondRow);
